// SPDX-License-Identifier: AGPL-3.0-or-later

import {EmailVerificationAlert} from '@app/features/app/components/dialogs/components/EmailVerificationAlert';
import {UnclaimedAccountAlert} from '@app/features/app/components/dialogs/components/UnclaimedAccountAlert';
import {SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {openClaimAccountModal} from '@app/features/auth/components/modals/ClaimAccountModal';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {EmailChangeModal} from '@app/features/user/components/modals/EmailChangeModal';
import {PasswordChangeModal} from '@app/features/user/components/modals/PasswordChangeModal';
import styles from '@app/features/user/components/modals/tabs/account_security_tab/AccountTab.module.css';
import type {User} from '@app/features/user/models/User';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const HIDE_EMAIL_TOGGLE_DESCRIPTOR = msg({
	message: 'Hide',
	comment: 'Account settings: button that hides the user email address (currently revealed). Verb, sentence case.',
});
const REVEAL_EMAIL_TOGGLE_DESCRIPTOR = msg({
	message: 'Reveal',
	comment: 'Account settings: button that reveals the masked user email address. Verb, sentence case.',
});
const EMAIL_SETTINGS_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Manage the email address you use to sign in to {productName}',
	comment: 'Account settings section description. productName is the Fluxer product name.',
});
const maskEmail = (email: string): string => {
	const [username, domain] = email.split('@');
	const maskedUsername = username.replace(/./g, '*');
	return `${maskedUsername}@${domain}`;
};

interface AccountTabProps {
	user: User;
	isClaimed: boolean;
	showMaskedEmail: boolean;
	setShowMaskedEmail: (show: boolean) => void;
}

export const AccountTabContent: React.FC<AccountTabProps> = observer(
	({user, isClaimed, showMaskedEmail, setShowMaskedEmail}) => {
		const {i18n} = useLingui();
		return (
			<>
				{!isClaimed && (
					<UnclaimedAccountAlert data-flx="user.account-security-tab.account-tab.account-tab-content.unclaimed-account-alert" />
				)}
				<SettingsTabSection
					title={<Trans>Email settings</Trans>}
					description={i18n._(EMAIL_SETTINGS_DESCRIPTION_DESCRIPTOR, {productName: PRODUCT_NAME})}
					data-flx="user.account-security-tab.account-tab.account-tab-content.settings-tab-section"
				>
					{isClaimed ? (
						<>
							<div className={styles.row} data-flx="user.account-security-tab.account-tab.account-tab-content.row">
								<div
									className={styles.rowContent}
									data-flx="user.account-security-tab.account-tab.account-tab-content.row-content"
								>
									<div
										className={styles.label}
										data-flx="user.account-security-tab.account-tab.account-tab-content.label"
									>
										<Trans>Email address</Trans>
									</div>
									<div
										className={styles.emailRow}
										data-flx="user.account-security-tab.account-tab.account-tab-content.email-row"
									>
										<span
											className={`${styles.emailText} ${showMaskedEmail ? styles.emailTextSelectable : ''}`}
											data-flx="user.account-security-tab.account-tab.account-tab-content.email-text"
										>
											{showMaskedEmail ? user.email : maskEmail(user.email!)}
										</span>
										<button
											type="button"
											className={styles.toggleButton}
											onClick={() => setShowMaskedEmail(!showMaskedEmail)}
											data-flx="user.account-security-tab.account-tab.account-tab-content.toggle-button.set-show-masked-email"
										>
											{showMaskedEmail ? i18n._(HIDE_EMAIL_TOGGLE_DESCRIPTOR) : i18n._(REVEAL_EMAIL_TOGGLE_DESCRIPTOR)}
										</button>
									</div>
								</div>
								<Button
									small={true}
									onClick={() =>
										ModalCommands.push(
											modal(() => (
												<EmailChangeModal
													user={user}
													data-flx="user.account-security-tab.account-tab.account-tab-content.email-change-modal"
												/>
											)),
										)
									}
									data-flx="user.account-security-tab.account-tab.account-tab-content.button.push"
								>
									<Trans>Change email</Trans>
								</Button>
							</div>
							{user.email && !user.verified && (
								<EmailVerificationAlert data-flx="user.account-security-tab.account-tab.account-tab-content.email-verification-alert" />
							)}
						</>
					) : (
						<div className={styles.row} data-flx="user.account-security-tab.account-tab.account-tab-content.row--2">
							<div
								className={styles.rowContent}
								data-flx="user.account-security-tab.account-tab.account-tab-content.row-content--2"
							>
								<div
									className={styles.label}
									data-flx="user.account-security-tab.account-tab.account-tab-content.label--2"
								>
									<Trans>Email address</Trans>
								</div>
								<div
									className={styles.warningText}
									data-flx="user.account-security-tab.account-tab.account-tab-content.warning-text"
								>
									<Trans>No email address set</Trans>
								</div>
							</div>
							<Button
								small={true}
								className={styles.claimButton}
								fitContent
								onClick={() => openClaimAccountModal()}
								data-flx="user.account-security-tab.account-tab.account-tab-content.claim-button.open-claim-account-modal"
							>
								<Trans>Add email</Trans>
							</Button>
						</div>
					)}
				</SettingsTabSection>
				<SettingsTabSection
					title={<Trans>Password</Trans>}
					description={<Trans>Change your password to keep your account secure</Trans>}
					data-flx="user.account-security-tab.account-tab.account-tab-content.settings-tab-section--2"
				>
					<div className={styles.row} data-flx="user.account-security-tab.account-tab.account-tab-content.row--3">
						{isClaimed ? (
							<>
								<div
									className={styles.rowContent}
									data-flx="user.account-security-tab.account-tab.account-tab-content.row-content--3"
								>
									<div
										className={styles.label}
										data-flx="user.account-security-tab.account-tab.account-tab-content.label--3"
									>
										<Trans>Current password</Trans>
									</div>
									<div
										className={styles.description}
										data-flx="user.account-security-tab.account-tab.account-tab-content.description"
									>
										{user.passwordLastChangedAt ? (
											<Trans>Last changed: {DateUtils.getRelativeDateString(user.passwordLastChangedAt, i18n)}</Trans>
										) : (
											<Trans>Last changed: never</Trans>
										)}
									</div>
								</div>
								<Button
									small={true}
									onClick={() =>
										ModalCommands.push(
											modal(() => (
												<PasswordChangeModal data-flx="user.account-security-tab.account-tab.account-tab-content.password-change-modal" />
											)),
										)
									}
									data-flx="user.account-security-tab.account-tab.account-tab-content.button.push--2"
								>
									<Trans>Change password</Trans>
								</Button>
							</>
						) : (
							<>
								<div
									className={styles.rowContent}
									data-flx="user.account-security-tab.account-tab.account-tab-content.row-content--4"
								>
									<div
										className={styles.label}
										data-flx="user.account-security-tab.account-tab.account-tab-content.label--4"
									>
										<Trans>Password</Trans>
									</div>
									<div
										className={styles.warningText}
										data-flx="user.account-security-tab.account-tab.account-tab-content.warning-text--2"
									>
										<Trans>No password set</Trans>
									</div>
								</div>
								<Button
									small={true}
									className={styles.claimButton}
									fitContent
									onClick={() => openClaimAccountModal()}
									data-flx="user.account-security-tab.account-tab.account-tab-content.claim-button.open-claim-account-modal--2"
								>
									<Trans>Set password</Trans>
								</Button>
							</>
						)}
					</div>
				</SettingsTabSection>
			</>
		);
	},
);
